import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Container } from 'reactstrap';
import AddZone from '../../../../../ProgramOnboarding/Components/SelectZone/Components/AddZone';
import { useDispatch, useSelector } from 'react-redux';
import { CREATE_ZONES_V2, UPDATE_ZONES_V2, VALIDATE_ZONE, VALIDATE_ZONE_EMPTY } from '../../../../../../../../../store/actions';
import { ChevronLeft } from 'react-feather';
import cogoToast from 'cogo-toast';
import { Loader } from '../../../../../../../../Common';

const getInitialZones = () => ({
  airport: null,
  Road: [{ name: '', zoneRange: [0, 25] }],
  Radial: [{ name: '', zoneRange: [0, 25] }],
  Polygon: { name: '', serviceableAreas: '', nonServiceableAreas: '' },
});

const AddAndUpdateZones = ({ currentSelectedInventory, onClose, isUpdate }) => {
  const dispatch = useDispatch();

  const [zones, setZones] = useState(getInitialZones());

  const [distanceType, setDistanceType] = useState('Road');
  const [errors, setErrors] = useState<any>({
    Road: [],
    Radial: [],
    Polygon: { name: false, serviceableAreas: false, nonServiceableAreas: false },
    airport: false,
  });
  const [payoadZones, setPayoadZones] = useState([]);

  const { validateZone } = useSelector((state: any) => ({
    validateZone: state.ValidateZone?.data,
  }));

  useEffect(() => {
    if (validateZone !== null) {
      const payload = {
        reference_airport_id: zones?.airport?.value,
        zone_details: payoadZones,
      };

      if (isUpdate) {
        dispatch({
          type: UPDATE_ZONES_V2,
          payload: {
            data: { ...payload, id: currentSelectedInventory?.id },
          },
        });
      } else {
        dispatch({
          type: CREATE_ZONES_V2,
          payload: {
            data: payload,
          },
        });
      }

      dispatch({
        type: VALIDATE_ZONE_EMPTY,
      });
    }
  }, [validateZone]);

  useEffect(() => {
    if (currentSelectedInventory) {
      const selectedType = currentSelectedInventory?.distanceType;

      // Base structure with airport
      const updatedZones = {
        airport: currentSelectedInventory?.airport
          ? { value: currentSelectedInventory?.airport?.id, label: currentSelectedInventory?.airport?.name }
          : null,
        Road: [],
        Radial: [],
        Polygon: { name: '', serviceableAreas: '', nonServiceableAreas: '' },
      };

      // Set only the available type
      if (selectedType === 'Road') {
        updatedZones.Road = [{ name: currentSelectedInventory?.name, zoneRange: Object?.values(currentSelectedInventory?.zoneRange ?? {}) }];
      } else if (selectedType === 'Radial') {
        updatedZones.Radial = [{ name: currentSelectedInventory?.name, zoneRange: Object?.values(currentSelectedInventory?.zoneRange ?? {}) }];
      } else if (selectedType === 'Polygon') {
        updatedZones.Polygon = {
          name: currentSelectedInventory?.name,
          serviceableAreas: currentSelectedInventory?.serviceableAreas ? JSON.stringify(currentSelectedInventory?.serviceableAreas) : '',
          nonServiceableAreas: currentSelectedInventory?.nonServiceableAreas ? JSON.stringify(currentSelectedInventory?.nonServiceableAreas) : '',
        };
      }

      setZones(updatedZones);
      setDistanceType(selectedType); // optional: if you want to set this too
    }
  }, [currentSelectedInventory]);

  const isValidGeoJson = (geoJson: any): boolean => {
    if (!geoJson) return false;

    let parsed;
    try {
      parsed = typeof geoJson === 'string' ? JSON.parse(geoJson) : geoJson;
    } catch {
      return false; // Not valid JSON
    }

    // Must be a FeatureCollection
    if (parsed?.type !== 'FeatureCollection' || !Array.isArray(parsed?.features)) return false;

    // At least one feature required
    if (parsed.features.length === 0) return false;

    // Validate each feature
    for (const feature of parsed.features) {
      if (feature?.type !== 'Feature') return false;

      const geometry = feature?.geometry;
      if (!geometry || geometry.type !== 'Polygon') return false;

      const coords = geometry.coordinates;
      if (!Array.isArray(coords) || coords.length === 0) return false;

      const ring = coords[0];
      if (!Array.isArray(ring) || ring.length < 4) return false;

      // Each point must be [number, number]
      const arePointsValid = ring.every(
        (point: any) => Array.isArray(point) && point.length === 2 && typeof point[0] === 'number' && typeof point[1] === 'number',
      );

      if (!arePointsValid) return false;
    }

    return true;
  };

  const isAnyZoneValid = () => {
    const currentZones = zones[distanceType];

    const currentZonesForRoad = zones['Road'];
    const currentZonesForRadial = zones['Radial'];

    // ✅ Airport validation
    if (!zones?.airport) {
      setErrors(prev => ({
        ...prev,
        airport: true,
      }));
      return false;
    }

    // ✅ Road zone range validation
    if (currentZonesForRoad.length > 0) {
      const lastZoneForRoad = currentZonesForRoad[currentZonesForRoad.length - 1];
      if (lastZoneForRoad.zoneRange[0] === 0 && lastZoneForRoad.zoneRange[1] === 0) {
        cogoToast.error('Start and End cannot both be 0.');
        return false;
      }
    }

    // ✅ Radial zone range validation
    if (currentZonesForRadial.length > 0) {
      const lastZoneForRadial = currentZonesForRadial[currentZonesForRadial.length - 1];
      if (lastZoneForRadial.zoneRange[0] === 0 && lastZoneForRadial.zoneRange[1] === 0) {
        cogoToast.error('Start and End cannot both be 0.');
        return false;
      }
    }

    const nameErrors = Array.isArray(currentZones) ? currentZones.map(zone => !zone?.name?.trim()) : [];

    // ✅ Polygon validation
    const polygonTouched = zones?.Polygon?.name?.trim() || zones?.Polygon?.serviceableAreas?.trim() || zones?.Polygon?.nonServiceableAreas?.trim();

    const shouldValidatePolygon = distanceType === 'Polygon' || polygonTouched;

    const polygonErrors = {
      name: shouldValidatePolygon ? !zones?.Polygon?.name?.trim() : false,
      serviceableAreas: shouldValidatePolygon ? !isValidGeoJson(zones?.Polygon?.serviceableAreas) : false,
      // ✅ only validate nonServiceableAreas if entered
      nonServiceableAreas:
        shouldValidatePolygon && zones?.Polygon?.nonServiceableAreas?.trim() ? !isValidGeoJson(zones?.Polygon?.nonServiceableAreas) : false,
    };

    const hasError = nameErrors.includes(true) || polygonErrors.name || polygonErrors.serviceableAreas || polygonErrors.nonServiceableAreas;

    if (hasError) {
      setErrors(prev => ({
        ...prev,
        [distanceType]: nameErrors,
        Polygon: polygonErrors,
      }));
      return false;
    }

    // ✅ Zone validity check
    const isValidZone = (zone: any, type: string): boolean => {
      if (!zone?.name?.trim()) return false;
      if (type === 'Polygon') {
        return (
          !!zone?.serviceableAreas?.trim() &&
          isValidGeoJson(zone?.serviceableAreas) &&
          // nonServiceableAreas optional
          (!zone?.nonServiceableAreas?.trim() || isValidGeoJson(zone?.nonServiceableAreas))
        );
      }
      return Array.isArray(zone.zoneRange) && zone.zoneRange[1] > zone.zoneRange[0];
    };

    const isRoadValid = zones?.Road?.some(zone => isValidZone(zone, 'Road'));
    const isRadialValid = zones?.Radial?.some(zone => isValidZone(zone, 'Radial'));
    const isPolygonValid = shouldValidatePolygon && zones.Polygon && isValidZone(zones.Polygon, 'Polygon');

    const isAnyValid = isRoadValid || isRadialValid || isPolygonValid;

    if (!isAnyValid) {
      cogoToast.error('Please configure at least one valid zone.');
      return false;
    }

    return true;
  };

  const handleZone = () => {
    const error = isAnyZoneValid();
    if (!error) return;

    const result: {
      name: string;
      distanceType: 'Road' | 'Radial' | 'Polygon';
      zoneRange?: { from: number; to: number };
      serviceableAreas?: any;
      nonServiceableAreas?: any;
    }[] = [];

    const collectZones = (zoneType: 'Road' | 'Radial') => {
      zones[zoneType]?.forEach(zone => {
        if (zone?.name) {
          result.push({
            name: zone.name,
            distanceType: zoneType,
            zoneRange: {
              from: zone.zoneRange?.[0] ?? 0,
              to: zone.zoneRange?.[1] ?? 0,
            },
          });
        }
      });
    };

    collectZones('Road');
    collectZones('Radial');

    // ✅ Polygon handling
    if (zones['Polygon']?.name && zones['Polygon'].serviceableAreas) {
      try {
        const serviceableAreas = JSON.parse(zones['Polygon'].serviceableAreas);

        const polygonData: any = {
          name: zones['Polygon'].name,
          distanceType: 'Polygon',
          serviceableAreas,
        };

        // ✅ only include nonServiceableAreas if entered
        if (zones['Polygon'].nonServiceableAreas?.trim()) {
          polygonData.nonServiceableAreas = JSON.parse(zones['Polygon'].nonServiceableAreas);
        }

        result.push(polygonData);
      } catch (err) {
        console.error('Invalid GeoJSON:', err);
        cogoToast.error('Invalid GeoJSON format for Polygon.');
        return;
      }
    }

    setPayoadZones(result);

    dispatch({
      type: VALIDATE_ZONE,
      payload: {
        data: {
          reference_airport_id: zones.airport?.value,
          zone_details: result,
        },
      },
    });
  };

  return (
    <Card className="p-3">
      <CardHeader tag="h5" className="border-bottom pb-2">
        {isUpdate ? 'Update Zones' : 'Add Zones'}
      </CardHeader>
      <CardBody>
        <AddZone
          zones={zones}
          setZones={setZones}
          errors={errors}
          setErrors={setErrors}
          activeAirport={{ name: zones?.airport?.label }}
          distanceType={distanceType}
          setDistanceType={setDistanceType}
          isUpdate={isUpdate}
        />{' '}
        <div className="d-flex justify-content-between mt-4">
          <Button
            onClick={() => {
              setZones(getInitialZones());
              onClose();
            }}
            color="light"
            className="d-flex align-items-center gap-1 px-3 py-2"
            style={{
              backgroundColor: '#E9EBEC', // light gray
              color: '#404B7C', // navy/dark blue
              fontWeight: 500,
              boxShadow: 'none',
              border: 'none',
            }}
          >
            <ChevronLeft size={18} />
            Back
          </Button>
          <Button className="btn btn-primary" color="primary" onClick={handleZone}>
            {isUpdate ? 'Update Zones' : 'Add Zones'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default AddAndUpdateZones;
